import { Capacitor, type PluginListenerHandle, registerPlugin } from "@capacitor/core";

export type MobileSsoBrowser = "default" | "edge" | "in_app";

interface MobileSsoPlugin {
    claimCallback(options: { id: string }): Promise<{ claimed: boolean }>;
    open(options: { url: string; browser: "default" | "edge" }): Promise<void>;
}

const MobileSso = registerPlugin<MobileSsoPlugin>("MobileSso");

export class MobileSsoUpdateRequired extends Error {}

export async function launchMobileSso(url: string, browser: MobileSsoBrowser, onFinished: () => void, externalUrl?: string | null): Promise<PluginListenerHandle | null> {
    // Only the explicit external URL opts into the new server callback.
    if (browser === "edge" && !externalUrl) {
        throw new Error("Required Edge sign-in URL is missing");
    }
    if (!externalUrl) {
        browser = "in_app";
    }
    if (externalUrl && browser !== "in_app" && Capacitor.isPluginAvailable("MobileSso")) {
        await MobileSso.open({ url: externalUrl, browser });
        return null;
    }
    // Remote frontend updates reach old binaries too. Never silently bypass an Edge requirement.
    if (browser === "edge" || !Capacitor.isPluginAvailable("Browser")) {
        throw new MobileSsoUpdateRequired();
    }
    const { Browser } = await import("@capacitor/browser");
    const listener = await Browser.addListener("browserFinished", onFinished);
    try {
        await Browser.open({ url });
        return listener;
    } catch (error) {
        await listener.remove();
        throw error;
    }
}

export async function openEdgeInstallPage(): Promise<void> {
    await MobileSso.open({
        url: Capacitor.getPlatform() === "ios" ? "https://apps.apple.com/app/id1288723196" : "https://play.google.com/store/apps/details?id=com.microsoft.emmx",
        browser: "default",
    });
}

// Native process state survives WebView navigations between regions. Store only
// a digest, never the sign-in token, and reset naturally when the app restarts.
export async function claimMobileSsoCallback(url: string): Promise<boolean> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(url));
    const id = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    return (await MobileSso.claimCallback({ id })).claimed;
}
